<script setup lang="ts">
/**
 * BillingSection — Settings > Billing
 *
 * Displays the current Stripe billing state:
 *   - Plan name + Status badge (Active=green, PastDue=amber, Cancelled=red, Trial=navy)
 *   - Home cap + renewal date (font-mono tabular-nums per CLAUDE.md)
 *   - Past-due amber warning banner
 *   - "Manage billing" button → calls store.requestBillingPortal() → redirects to Stripe portal
 *   - "Change plan" / "Cancel" buttons → stub modal with "Contact support" copy
 *
 * No AI content → NO AI badges in this component.
 * Teal is AI-signal only → NOT used here.
 */

import { ref, computed, onMounted } from 'vue';
import { useSettingsStore } from '@/stores/settings';
import BaseBadge from '@/components/ui/BaseBadge.vue';
import BaseButton from '@/components/ui/BaseButton.vue';

const store = useSettingsStore();

// ── Local modal state ─────────────────────────────────────────────────────────
const showContactModal = ref(false);
const contactModalTitle = ref('');
const portalError = ref<string | null>(null);
const isPortalLoading = ref(false);

// ── Computed helpers ──────────────────────────────────────────────────────────

const billing = computed(() => store.billing);

/**
 * Maps BillingStatus to the BaseBadge variant.
 * Active = green (resolved), PastDue = medium (amber), Cancelled = critical (red), Trial = low (navy)
 */
function statusBadgeVariant(status: string): 'resolved' | 'medium' | 'critical' | 'low' {
  switch (status) {
    case 'Active':    return 'resolved';
    case 'PastDue':   return 'medium';
    case 'Cancelled': return 'critical';
    default:          return 'low'; // Trial
  }
}

const isPastDue = computed(() => billing.value?.status === 'PastDue');

const renewalLabel = computed(() => {
  if (!billing.value?.renewsAt) return null;
  return new Date(billing.value.renewsAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
});

// ── Actions ───────────────────────────────────────────────────────────────────

async function handleManageBilling(): Promise<void> {
  isPortalLoading.value = true;
  portalError.value = null;
  try {
    await store.requestBillingPortal();
    // On success this triggers window.location.href = url (redirect to Stripe portal)
  } catch (e) {
    portalError.value = e instanceof Error ? e.message : 'Could not open billing portal.';
  } finally {
    isPortalLoading.value = false;
  }
}

function openContactModal(title: string): void {
  contactModalTitle.value = title;
  showContactModal.value = true;
}

function closeContactModal(): void {
  showContactModal.value = false;
}

// ── Load billing state on mount (if not already loaded) ───────────────────────
onMounted(async () => {
  if (!billing.value) {
    await store.fetch();
  }
});
</script>

<template>
  <section id="billing" data-testid="billing-section" class="scroll-mt-20">
    <!-- Section header -->
    <div class="mb-6">
      <h2 class="text-xl font-semibold text-fg">Billing</h2>
      <p class="text-sm text-muted mt-1">
        Manage your subscription plan and billing details.
      </p>
    </div>

    <!-- Past-due warning banner -->
    <div
      v-if="isPastDue"
      class="mb-5 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
      role="alert"
      data-testid="past-due-banner"
    >
      <svg class="mt-0.5 h-5 w-5 shrink-0 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
      <div>
        <p class="font-semibold text-amber-800">Payment overdue</p>
        <p class="mt-0.5 text-sm text-amber-700">
          Your last invoice payment failed. Please update your payment method to avoid service interruption.
        </p>
        <BaseButton
          variant="secondary"
          size="sm"
          class="mt-2"
          :disabled="isPortalLoading"
          @click="handleManageBilling"
        >
          Update payment method
        </BaseButton>
      </div>
    </div>

    <!-- Plan card -->
    <div class="rounded-xl border border-border bg-surface p-6" data-testid="billing-plan-card">
      <div v-if="!billing" class="text-sm text-muted">
        Loading billing information&hellip;
      </div>

      <template v-else>
        <!-- Plan name + status badge -->
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-3">
            <span class="text-xl font-semibold text-fg font-mono tabular-nums" data-testid="plan-name">
              {{ billing.plan }}
            </span>
            <BaseBadge
              :variant="statusBadgeVariant(billing.status ?? 'Trial')"
              data-testid="billing-status-badge"
            >
              {{ billing.status ?? 'Trial' }}
            </BaseBadge>
          </div>
          <div class="flex gap-2">
            <BaseButton
              variant="secondary"
              size="sm"
              data-testid="change-plan-button"
              @click="openContactModal('Change plan')"
            >
              Change plan
            </BaseButton>
            <BaseButton
              variant="ghost"
              size="sm"
              data-testid="cancel-plan-button"
              @click="openContactModal('Cancel subscription')"
            >
              Cancel
            </BaseButton>
          </div>
        </div>

        <!-- Usage stats -->
        <div class="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div class="rounded-lg border border-border bg-canvas-soft px-4 py-3">
            <dt class="text-caption text-muted">Home cap</dt>
            <dd class="mt-1 text-lg font-semibold font-mono tabular-nums text-fg" data-testid="home-cap">
              {{ billing.homeCap.toLocaleString() }}
            </dd>
          </div>
          <div v-if="renewalLabel" class="rounded-lg border border-border bg-canvas-soft px-4 py-3">
            <dt class="text-caption text-muted">Renews</dt>
            <dd class="mt-1 text-lg font-semibold font-mono tabular-nums text-fg" data-testid="renews-at">
              {{ renewalLabel }}
            </dd>
          </div>
        </div>

        <!-- Manage billing portal link -->
        <div class="mt-5 flex items-center gap-3 border-t border-border pt-5">
          <BaseButton
            variant="secondary"
            size="md"
            :disabled="isPortalLoading"
            data-testid="manage-billing-button"
            @click="handleManageBilling"
          >
            <svg v-if="isPortalLoading" class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Manage billing
          </BaseButton>
          <p class="text-caption text-muted">Billing managed by Stripe</p>
        </div>

        <!-- Portal error -->
        <p v-if="portalError" class="mt-3 text-sm text-red-600" role="alert" data-testid="portal-error">
          {{ portalError }}
        </p>
      </template>
    </div>

    <!-- "Contact support" stub modal for Change plan / Cancel -->
    <div
      v-if="showContactModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      :aria-label="contactModalTitle"
      data-testid="contact-modal"
    >
      <div class="w-full max-w-sm rounded-xl border border-border bg-surface p-6 shadow-xl">
        <h3 class="text-lg font-semibold text-fg">{{ contactModalTitle }}</h3>
        <p class="mt-3 text-sm text-muted">
          To {{ contactModalTitle.toLowerCase() }}, please contact our support team and
          we'll help you right away.
        </p>
        <div class="mt-5 flex justify-end gap-2">
          <BaseButton variant="secondary" size="sm" @click="closeContactModal">
            Close
          </BaseButton>
          <a
            href="mailto:support@hoawatch.us"
            class="inline-flex items-center justify-center h-8 px-3 text-caption font-medium rounded-sm bg-navy-800 text-white hover:bg-navy-900 transition-colors"
          >
            Contact support
          </a>
        </div>
      </div>
    </div>
  </section>
</template>
